import { DemoProfilePickerModal } from '../components/DemoProfilePickerModal';
import { Money } from '../components/Money';
import { WebHead } from '../components/WebHead';
import { Ionicons } from '@expo/vector-icons';
import { Redirect, type Href, useLocalSearchParams, usePathname, useRouter } from 'expo-router';
import { createElement, type CSSProperties, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { DEMO_ACCOUNTS, type DemoAccountKey } from '@perdiem/trpc/demo-accounts';
import {
  Image,
  type ImageSourcePropType,
  type LayoutChangeEvent,
  type NativeScrollEvent,
  type NativeSyntheticEvent,
  Platform,
  Pressable,
  ScrollView,
  type StyleProp,
  Text,
  type TextStyle,
  useWindowDimensions,
  View,
  type ViewStyle,
} from 'react-native';
import { setPendingMarketingAttribution } from '../lib/marketing-attribution';
import {
  trackMarketingCtaClicked,
  trackMarketingPageViewed,
} from '../lib/product-events';
import { signInDemoAccount } from '../lib/supabase';
import { getLegalUrl, openExternalUrl } from '../lib/utils';

const CHATGPT_APP_URL = 'https://chatgpt.com/apps/per-diem/asdk_app_69ee4577626081919c98e6222ff8785c';
const CHATGPT_DEMO_EMBED_URL = 'https://www.youtube.com/embed/4Lvgs9uKbFs?rel=0&modestbranding=1';
const AI_CONNECTORS_ROUTE = '/ai-connectors' as Href;
const LOGIN_ROUTE = '/(auth)/login' as Href;
const SHOW_SERVICE_OUTAGE_BANNER = false;
const LOOPBACK_HOSTS = new Set(['localhost', '127.0.0.1']);
const MOBILE_APP_LINKS = {
  ios: 'https://apps.apple.com/us/app/per-diem-path-to-fi/id6749600123',
  android: 'https://play.google.com/store/apps/details?id=com.dadmakefire.perdiem',
} as const;

const X_URL = 'https://x.com/perdiem_app';
const GUIDES_ROUTE = '/guides/' as Href;
const COMPARE_ROUTE = '/compare/' as Href;
const SUPPORT_ROUTE = '/support' as Href;
const LEGAL_LINKS = {
  privacy: getLegalUrl('privacy') as Href,
  security: getLegalUrl('security') as Href,
  terms: getLegalUrl('terms') as Href,
};

const FIRE_PLAN_IMAGE = require('../assets/screenshots/mobile-fire-plan.png');
const MOBILE_HOME_IMAGE = require('../assets/screenshots/mobile-home.png');
const MOBILE_FIRE_IMAGE = require('../assets/screenshots/mobile-fire-plan-redesign.png');
const MOBILE_AI_IMAGE = require('../assets/screenshots/mobile-ask-ai.png');
const CHASE_LOGO = require('../assets/institutions/chase.png');
const FIDELITY_LOGO = require('../assets/institutions/fidelity.png');
const CITIBANK_LOGO = require('../assets/institutions/citibank.png');
const STORE_BADGES = {
  ios: require('../assets/store/badges/app-store-badge.png'),
  android: require('../assets/store/badges/google-play-badge.png'),
} as const;

type SectionId = 'top' | 'connect' | 'daily' | 'plan' | 'scenarios' | 'phone' | 'chatgpt' | 'compare' | 'faq' | 'start';

const SECTION_IDS: SectionId[] = ['top', 'connect', 'daily', 'plan', 'scenarios', 'phone', 'chatgpt', 'compare', 'faq', 'start'];

const ACCOUNT_ROWS: Array<{
  logo: ImageSourcePropType;
  name: string;
  detail: string;
  value: string;
  note: string;
  tone: 'up' | 'muted';
}> = [
  { logo: CHASE_LOGO, name: 'Chase checking', detail: 'synced recently', value: '$8,412', note: '+$265 today', tone: 'up' },
  { logo: FIDELITY_LOGO, name: 'Fidelity 401(k)', detail: 'investment balance', value: '$284,910', note: '+0.4% today', tone: 'up' },
  { logo: CITIBANK_LOGO, name: 'Citi savings', detail: 'cash reserve', value: '$48,224', note: 'reserved cash', tone: 'muted' },
  { logo: CHASE_LOGO, name: 'Chase Sapphire', detail: 'credit card', value: '$1,284', note: 'new activity', tone: 'muted' },
  { logo: FIDELITY_LOGO, name: 'Fidelity brokerage', detail: 'taxable account', value: '$162,408', note: '+0.3% today', tone: 'up' },
];

const COMPARE_ROWS = [
  { feature: 'Daily spending guidance', perdiem: true, monarch: false, projectionlab: false, tiller: false },
  { feature: 'Budget tracking', perdiem: true, monarch: true, projectionlab: false, tiller: true },
  { feature: 'Live account data', perdiem: true, monarch: true, projectionlab: false, tiller: true },
  { feature: 'FIRE projections', perdiem: true, monarch: false, projectionlab: true, tiller: false },
  { feature: 'Saved scenario planning', perdiem: true, monarch: false, projectionlab: true, tiller: false },
  { feature: 'Monte Carlo simulations', perdiem: true, monarch: false, projectionlab: true, tiller: false },
  { feature: 'Chat with your finances', perdiem: true, monarch: false, projectionlab: false, tiller: false },
  { feature: 'Native iOS and Android apps', perdiem: true, monarch: true, projectionlab: false, tiller: false },
  { feature: 'Annual price', perdiem: '$89', monarch: '$99', projectionlab: '$109', tiller: '$79' },
] as const;

const COMPARE_COLUMNS = [
  { key: 'perdiem', label: 'Per Diem' },
  { key: 'monarch', label: 'Monarch' },
  { key: 'projectionlab', label: 'ProjectionLab' },
  { key: 'tiller', label: 'Tiller' },
] as const;

const FAQS = [
  {
    question: 'How is the per diem calculated?',
    answer:
      'Per Diem starts with household income, fixed expenses, discretionary budget, spending already logged this month, and the days left in the month. The result is one daily spending number.',
  },
  {
    question: 'What happens if we spend over it?',
    answer:
      'The next number adjusts. If you spend below plan, unused room rolls forward. If you spend above plan, the remaining month tightens automatically.',
  },
  {
    question: 'How is this different from Monarch or ProjectionLab?',
    answer:
      'Monarch is strong for budgeting. ProjectionLab is strong for planning. Per Diem is for the FIRE community: people who want daily spending and long-range planning connected to the same real account data.',
  },
  {
    question: 'Can I try it before using real accounts?',
    answer:
      'Yes. Demo profiles let you explore the dashboard, budget, accounts, tax context, and FIRE plan with sample households before connecting your own accounts.',
  },
  {
    question: 'Can I model what-if scenarios?',
    answer:
      'Yes. Save scenarios for changes like spending, income, holdings, state moves, job pauses, or life events, then run the FIRE projection without changing your default plan.',
  },
  {
    question: 'Can ChatGPT change my data?',
    answer:
      'No. The ChatGPT app can answer from your authorized Per Diem context, but it cannot move money, trade, edit, or delete your records.',
  },
  {
    question: 'Is my data secure?',
    answer:
      'Per Diem uses hosted authentication, read-only financial connections, and encrypted token storage. Raw bank credentials stay with the connection provider, not Per Diem, and the app cannot move money or place trades.',
  },
] as const;

const COLORS = {
  bg: '#0A0E0B',
  bg2: '#0E1410',
  ink: '#F2F4EF',
  ink2: 'rgba(242,244,239,0.68)',
  ink3: 'rgba(242,244,239,0.46)',
  line: 'rgba(242,244,239,0.1)',
  green: '#3CB778',
  greenDeep: '#1F5237',
  amber: '#E5B85C',
  sky: '#5E8FB8',
  red: '#C25A52',
};

const DISPLAY_FONT = "'Instrument Serif', Georgia, serif";
const MONO_FONT = 'DMMono_400Regular';
const MONO_FONT_MEDIUM = 'DMMono_500Medium';
const PHONE_FRAME_PADDING = 20;
const PHONE_SCREEN_HEIGHT_RATIO = 1312 / 604;
const FIRE_PLAN_SCREEN_HEIGHT_RATIO = 1384 / 642;

function phoneShellHeight(width: number, screenHeightRatio = PHONE_SCREEN_HEIGHT_RATIO) {
  return Math.round((width - PHONE_FRAME_PADDING) * screenHeightRatio + PHONE_FRAME_PADDING);
}

function webStyle(style: Record<string, unknown>): ViewStyle {
  return Platform.OS === 'web' ? (style as unknown as ViewStyle) : {};
}

function webTextStyle(style: Record<string, unknown>): TextStyle {
  return Platform.OS === 'web' ? (style as unknown as TextStyle) : {};
}

function sectionBackground(kind: SectionId): ViewStyle {
  const backgrounds: Record<SectionId, string> = {
    top:
      'radial-gradient(ellipse at 50% 0%, rgba(94,143,184,0.22) 0%, transparent 58%), radial-gradient(ellipse at 50% 20%, rgba(229,184,92,0.10) 0%, transparent 42%), linear-gradient(180deg, #101A1F 0%, #0A0E0B 64%, #08100B 100%)',
    daily:
      'radial-gradient(ellipse at 50% 0%, rgba(60,183,120,0.10) 0%, transparent 62%), linear-gradient(180deg, #0A0E0B 0%, #0A120C 100%)',
    connect:
      'radial-gradient(ellipse at 20% 28%, rgba(94,143,184,0.17) 0%, transparent 56%), radial-gradient(ellipse at 80% 80%, rgba(60,183,120,0.12) 0%, transparent 58%), linear-gradient(180deg, #0B1316 0%, #0A0E0B 100%)',
    plan:
      'radial-gradient(ellipse at 50% 100%, rgba(31,82,55,0.36) 0%, transparent 56%), linear-gradient(180deg, #0A0E0B 0%, #0B140D 100%)',
    scenarios:
      'radial-gradient(ellipse at 28% 18%, rgba(94,143,184,0.18) 0%, transparent 56%), radial-gradient(ellipse at 80% 82%, rgba(229,184,92,0.11) 0%, transparent 58%), linear-gradient(180deg, #0B1118 0%, #0A0E0B 100%)',
    phone:
      'radial-gradient(ellipse at 76% 24%, rgba(229,184,92,0.14) 0%, transparent 56%), radial-gradient(ellipse at 22% 80%, rgba(60,183,120,0.10) 0%, transparent 58%), linear-gradient(180deg, #120F0A 0%, #0A0E0B 100%)',
    chatgpt:
      'radial-gradient(ellipse at 72% 26%, rgba(94,143,184,0.18) 0%, transparent 56%), radial-gradient(ellipse at 18% 82%, rgba(60,183,120,0.10) 0%, transparent 58%), linear-gradient(180deg, #0B1218 0%, #0A0E0B 100%)',
    compare:
      'radial-gradient(ellipse at 50% 0%, rgba(60,183,120,0.10) 0%, transparent 62%), linear-gradient(180deg, #060906 0%, #0A0E0B 100%)',
    faq:
      'radial-gradient(ellipse at 52% 12%, rgba(94,143,184,0.08) 0%, transparent 58%), linear-gradient(180deg, #0A0E0B 0%, #0C120E 100%)',
    start:
      'radial-gradient(ellipse at 50% 0%, rgba(94,143,184,0.20) 0%, transparent 58%), radial-gradient(ellipse at 50% 30%, rgba(60,183,120,0.12) 0%, transparent 56%), linear-gradient(180deg, #0F1A22 0%, #0A0E0B 100%)',
  };

  return webStyle({ background: backgrounds[kind] });
}

function revealStyle(isVisible: boolean, delayMs = 0): ViewStyle {
  return {
    opacity: isVisible ? 1 : 0,
    transform: [{ translateY: isVisible ? 0 : 28 }],
    ...webStyle({
      transitionProperty: 'opacity, transform',
      transitionDuration: '900ms',
      transitionTimingFunction: 'cubic-bezier(.2,.7,.2,1)',
      transitionDelay: `${delayMs}ms`,
    }),
  };
}

function displayTextStyle(size: number, isMobile: boolean): TextStyle {
  return {
    color: COLORS.ink,
    fontFamily: DISPLAY_FONT,
    fontSize: size,
    fontWeight: '400',
    letterSpacing: 0,
    lineHeight: Math.round(size * (isMobile ? 1.02 : 0.98)),
  };
}

function monoCapsStyle(color = COLORS.ink3): TextStyle {
  return {
    color,
    fontFamily: MONO_FONT,
    fontSize: 11,
    letterSpacing: 2,
    textTransform: 'uppercase',
  };
}

function LeafMark({ size = 34 }: { size?: number }) {
  return (
    <View
      style={[
        styles.leafMark,
        { height: size, width: size, borderRadius: Math.max(8, size * 0.28) },
        webStyle({
          background: `linear-gradient(160deg, ${COLORS.green} 0%, ${COLORS.greenDeep} 100%)`,
          boxShadow: '0 12px 28px -10px rgba(60,183,120,0.65)',
        }),
      ]}
    >
      <Ionicons name="leaf-outline" size={Math.round(size * 0.56)} color="#ffffff" />
    </View>
  );
}

function Eyebrow({
  children,
  tone = 'green',
  align = 'start',
}: {
  children: string;
  tone?: 'green' | 'amber';
  align?: 'start' | 'center';
}) {
  const color = tone === 'amber' ? COLORS.amber : COLORS.green;
  return (
    <View style={[styles.eyebrow, align === 'center' ? styles.eyebrowCenter : null, { borderColor: `${color}55`, backgroundColor: `${color}14` }]}>
      <View style={[styles.eyebrowDot, { backgroundColor: color }]} />
      <Text style={monoCapsStyle(color)}>{children}</Text>
    </View>
  );
}

function ServiceOutageBanner({ isMobile }: { isMobile: boolean }) {
  if (!SHOW_SERVICE_OUTAGE_BANNER) return null;

  return (
    <View
      accessibilityRole="alert"
      style={[
        styles.serviceOutageBanner,
        isMobile ? styles.serviceOutageBannerMobile : null,
      ]}
    >
      <View style={[styles.serviceOutageInner, isMobile ? styles.serviceOutageInnerMobile : null]}>
        <Ionicons name="alert-circle-outline" size={isMobile ? 18 : 20} color="#06180D" />
        <Text style={[styles.serviceOutageText, isMobile ? styles.serviceOutageTextMobile : null]}>
          <Text style={styles.serviceOutageStrong}>Service interruption: </Text>
          Per Diem app access is temporarily degraded while Railway recovers from a platform outage.
          Sign-up, demo profiles, account sync, and in-app data may fail until the API is back.
        </Text>
      </View>
    </View>
  );
}

function SectionNumber({ label, hidden = false }: { label: string; hidden?: boolean }) {
  if (hidden) return null;

  return (
    <View style={styles.sectionNumber}>
      <View style={styles.sectionRule} />
      <Text style={monoCapsStyle()}>{label}</Text>
    </View>
  );
}

function BulletList({ items }: { items: string[] }) {
  return (
    <View style={styles.bulletList}>
      {items.map((item) => (
        <View key={item} style={styles.bulletRow}>
          <View style={styles.bulletDot} />
          <Text style={styles.bulletText}>{item}</Text>
        </View>
      ))}
    </View>
  );
}

function PrimaryButton({
  label,
  onPress,
  tone = 'light',
}: {
  label: string;
  onPress: () => void;
  tone?: 'light' | 'green';
}) {
  const isGreen = tone === 'green';
  return (
    <Pressable
      accessibilityRole="button"
      onPress={onPress}
      style={({ pressed }) => [
        styles.button,
        {
          backgroundColor: isGreen ? COLORS.green : COLORS.ink,
          borderColor: isGreen ? COLORS.green : COLORS.ink,
          transform: [{ translateY: pressed ? 1 : 0 }],
        },
        webStyle({
          boxShadow: isGreen
            ? '0 16px 32px -14px rgba(60,183,120,0.72)'
            : '0 16px 32px -14px rgba(242,244,239,0.52)',
        }),
      ]}
    >
      <Text style={[styles.buttonLabel, { color: '#06180D' }]}>{label}</Text>
      <Ionicons name="arrow-forward" size={15} color="#06180D" />
    </Pressable>
  );
}

function GhostButton({ label, onPress }: { label: string; onPress: () => void }) {
  return (
    <Pressable
      accessibilityRole="button"
      onPress={onPress}
      style={({ pressed }) => [
        styles.button,
        styles.ghostButton,
        { transform: [{ translateY: pressed ? 1 : 0 }] },
      ]}
    >
      <Text style={[styles.buttonLabel, { color: COLORS.ink }]}>{label}</Text>
    </Pressable>
  );
}

function PerDiemPreviewCard({ isMobile }: { isMobile: boolean }) {
  return (
    <View
      style={[
        styles.perDiemCard,
        {
          padding: isMobile ? 24 : 34,
          maxWidth: isMobile ? 390 : 470,
        },
        webStyle({
          background: 'linear-gradient(180deg, rgba(60,183,120,0.12) 0%, rgba(60,183,120,0.03) 100%)',
          boxShadow: '0 34px 90px -34px rgba(0,0,0,0.72)',
        }),
      ]}
    >
      <View style={styles.perDiemLabelRow}>
        <Text style={monoCapsStyle()}>Per diem - today</Text>
        <View style={styles.livePill}>
          <View style={styles.liveDot} />
          <Text style={monoCapsStyle(COLORS.green)}>Live</Text>
        </View>
      </View>
      <Money
        weight={500}
        numberOfLines={1}
        adjustsFontSizeToFit
        minimumFontScale={0.64}
        style={[
          styles.bigMoney,
          {
            fontSize: isMobile ? 68 : 86,
            lineHeight: isMobile ? 76 : 94,
          },
        ]}
      >
        $142.30
      </Money>
      <Text style={[styles.greenMono, { marginTop: 4 }]}>Updated from today's budget and spending.</Text>
      <View style={styles.progressTrack}>
        <View style={[styles.progressFill, { width: '62%' }]} />
      </View>
    </View>
  );
}

function AccountManifest({ isMobile }: { isMobile: boolean }) {
  return (
    <View style={[styles.accountManifest, { maxWidth: isMobile ? 420 : 530 }]}>
      <View style={styles.accountHead}>
        <Text style={monoCapsStyle()}>Household accounts</Text>
        <View style={styles.livePill}>
          <View style={styles.liveDot} />
          <Text style={monoCapsStyle(COLORS.green)}>Recently synced</Text>
        </View>
      </View>
      {ACCOUNT_ROWS.map((account) => (
        <View
          key={account.name}
          style={[
            styles.accountRow,
            webStyle({
              background: 'linear-gradient(180deg, rgba(242,244,239,0.055) 0%, rgba(242,244,239,0.02) 100%)',
            }),
          ]}
        >
          <View style={styles.accountLogoFrame}>
            <Image
              source={account.logo}
              resizeMode="contain"
              accessibilityIgnoresInvertColors
              style={styles.accountLogo}
            />
          </View>
          <View style={styles.accountMeta}>
            <Text numberOfLines={1} style={styles.accountName}>{account.name}</Text>
            <Text style={monoCapsStyle()}>{account.detail}</Text>
          </View>
          <View style={styles.accountValue}>
            <Money weight={500} style={styles.accountMoney}>{account.value}</Money>
            <Text
              style={[
                monoCapsStyle(account.tone === 'up' ? COLORS.green : COLORS.ink3),
                { fontSize: 9, letterSpacing: 1, textAlign: 'right' },
              ]}
            >
              {account.note}
            </Text>
          </View>
        </View>
      ))}
      <View style={styles.aggregator}>
        <View style={styles.flowLines}>
          <View style={styles.flowLine} />
          <View style={[styles.flowLine, { width: '74%' }]} />
          <View style={[styles.flowLine, { width: '54%' }]} />
        </View>
        <View
          style={[
            styles.aggregatorCard,
            webStyle({
              background: 'linear-gradient(180deg, rgba(60,183,120,0.18), rgba(60,183,120,0.05))',
              boxShadow: '0 18px 38px -20px rgba(60,183,120,0.62)',
            }),
          ]}
        >
          <LeafMark size={32} />
          <View>
            <Text style={monoCapsStyle()}>Today's per diem</Text>
            <Money weight={500} style={styles.aggregatorMoney}>$142.30</Money>
          </View>
        </View>
      </View>
    </View>
  );
}

function PhoneShell({
  source,
  width,
  accessibilityLabel,
  screenHeightRatio = PHONE_SCREEN_HEIGHT_RATIO,
  showNotch = true,
  style,
}: {
  source: ImageSourcePropType;
  width: number;
  accessibilityLabel: string;
  screenHeightRatio?: number;
  showNotch?: boolean;
  style?: StyleProp<ViewStyle>;
}) {
  return (
    <View
      style={[
        styles.phoneShell,
        {
          width,
          height: phoneShellHeight(width, screenHeightRatio),
        },
        webStyle({
          boxShadow: '0 38px 90px -34px rgba(0,0,0,0.85)',
        }),
        style,
      ]}
    >
      {showNotch ? <View style={styles.phoneNotch} /> : null}
      <Image
        source={source}
        resizeMode="cover"
        accessibilityLabel={accessibilityLabel}
        accessibilityIgnoresInvertColors
        style={styles.phoneImage}
      />
    </View>
  );
}

function FirePlanPreviewCard({ isMobile }: { isMobile: boolean }) {
  const phoneWidth = isMobile ? 240 : 300;
  return (
    <PhoneShell
      source={FIRE_PLAN_IMAGE}
      width={phoneWidth}
      screenHeightRatio={FIRE_PLAN_SCREEN_HEIGHT_RATIO}
      accessibilityLabel="Per Diem FIRE plan screen"
    />
  );
}

function ScenarioPlanningCard({ isMobile }: { isMobile: boolean }) {
  const rows = [
    {
      name: 'Cut spending 10%',
      detail: 'Discretionary budget scenario',
      fiYear: '2039',
      result: '2 years earlier',
      tone: 'green' as const,
    },
    {
      name: 'Pause income',
      detail: 'Sabbatical in 2029',
      fiYear: '2043',
      result: '2 years later',
      tone: 'amber' as const,
    },
    {
      name: 'Add life event',
      detail: 'Home project in 2032',
      fiYear: '2042',
      result: '1 year later',
      tone: 'amber' as const,
    },
  ];

  return (
    <View
      style={[
        styles.scenarioCard,
        {
          maxWidth: isMobile ? 420 : 540,
          padding: isMobile ? 20 : 26,
        },
        webStyle({
          background: 'linear-gradient(180deg, rgba(94,143,184,0.15) 0%, rgba(242,244,239,0.035) 100%)',
          boxShadow: '0 34px 90px -34px rgba(0,0,0,0.76)',
        }),
      ]}
    >
      <View style={styles.scenarioHeader}>
        <Text style={monoCapsStyle()}>Scenario planning</Text>
        <Text style={monoCapsStyle(COLORS.green)}>Saved what-ifs</Text>
      </View>
      <View style={styles.scenarioBaseline}>
        <View style={styles.scenarioBaselineCopy}>
          <Text style={monoCapsStyle()}>Default plan</Text>
          <Text style={styles.scenarioBaselineTitle}>FI in 2041</Text>
        </View>
        <View style={styles.scenarioBaselineBadge}>
          <Text style={monoCapsStyle(COLORS.ink)}>Baseline</Text>
        </View>
      </View>
      <View style={styles.scenarioRows}>
        {rows.map((row) => (
          <View key={row.name} style={styles.scenarioRow}>
            <View style={styles.scenarioRowCopy}>
              <Text numberOfLines={1} style={styles.scenarioName}>{row.name}</Text>
              <Text numberOfLines={1} style={monoCapsStyle()}>{row.detail}</Text>
            </View>
            <View style={styles.scenarioOutcome}>
              <Text style={styles.scenarioYear}>{row.fiYear}</Text>
              <Text
                numberOfLines={1}
                style={[
                  styles.scenarioResult,
                  { color: row.tone === 'green' ? COLORS.green : COLORS.amber },
                ]}
              >
                {row.result}
              </Text>
            </View>
          </View>
        ))}
      </View>
    </View>
  );
}

function PhoneStack({ isMobile }: { isMobile: boolean }) {
  const phoneWidth = isMobile ? 224 : 286;
  return (
    <View style={[styles.phoneStack, { minHeight: isMobile ? 620 : 760 }]}>
      {[MOBILE_HOME_IMAGE, MOBILE_FIRE_IMAGE, MOBILE_AI_IMAGE].map((source, index) => (
        <PhoneShell
          key={index}
          source={source}
          width={phoneWidth}
          accessibilityLabel="Per Diem mobile app screen"
          style={{
            marginLeft: index === 0 ? 0 : isMobile ? -168 : -214,
            marginTop: index === 1 ? (isMobile ? 34 : 46) : index === 2 ? (isMobile ? 70 : 92) : 0,
            opacity: index === 0 ? 1 : 0.74,
            zIndex: 3 - index,
          }}
        />
      ))}
    </View>
  );
}

function HeroPhonePreview({ isMobile, isStacked }: { isMobile: boolean; isStacked: boolean }) {
  const phoneWidth = isMobile ? 250 : isStacked ? 300 : 326;
  const cropHeight = isMobile ? 370 : isStacked ? 560 : 620;

  return (
    <View style={[styles.heroPhoneStage, { height: cropHeight }]}>
      <View
        pointerEvents="none"
        style={[
          styles.heroPhoneHalo,
          webStyle({
            background:
              'radial-gradient(circle, rgba(60,183,120,0.19) 0%, rgba(94,143,184,0.08) 42%, transparent 72%)',
          }),
        ]}
      />
      <PhoneShell
        source={MOBILE_HOME_IMAGE}
        width={phoneWidth}
        showNotch={false}
        accessibilityLabel="Per Diem dashboard showing today's spending number and financial independence age"
        style={[
          styles.heroPhone,
          {
            transform: [{ rotate: isMobile ? '0deg' : '1.4deg' }],
          },
          webStyle({
            boxShadow:
              '0 54px 120px -38px rgba(0,0,0,0.92), 0 24px 70px -38px rgba(60,183,120,0.72)',
          }),
        ]}
      />
    </View>
  );
}

function MarketingVideo() {
  if (Platform.OS !== 'web') return null;

  return createElement('iframe', {
    src: CHATGPT_DEMO_EMBED_URL,
    title: 'ChatGPT Per Diem App demo',
    allow: 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share',
    allowFullScreen: true,
    loading: 'lazy',
    referrerPolicy: 'strict-origin-when-cross-origin',
    style: {
      border: 0,
      display: 'block',
      height: '100%',
      width: '100%',
    },
  });
}

function ChatGptVideoCard({ isMobile }: { isMobile: boolean }) {
  return (
    <View
      style={[
        styles.chatGptVideoCard,
        { maxWidth: isMobile ? 420 : 560 },
        webStyle({
          boxShadow: '0 34px 90px -34px rgba(0,0,0,0.76)',
        }),
      ]}
    >
      <View style={styles.videoFrame}>
        <MarketingVideo />
      </View>
    </View>
  );
}

function ComparisonTable({ isMobile }: { isMobile: boolean }) {
  const featureWidth = isMobile ? 150 : 260;
  const columnWidth = isMobile ? 88 : 130;

  return (
    <ScrollView horizontal showsHorizontalScrollIndicator={false}>
      <View
        style={[
          styles.compareTable,
          { minWidth: featureWidth + COMPARE_COLUMNS.length * columnWidth },
        ]}
      >
        <View style={styles.compareRow}>
          <View style={[styles.compareFeatureCell, { width: featureWidth }]} />
          {COMPARE_COLUMNS.map((column) => (
            <View key={column.key} style={[styles.compareCell, { width: columnWidth }]}>
              <Text
                style={[
                  styles.compareHead,
                  column.key === 'perdiem' ? { color: COLORS.green } : null,
                ]}
              >
                {column.label}
              </Text>
            </View>
          ))}
        </View>
        {COMPARE_ROWS.map((row) => (
          <View key={row.feature} style={styles.compareRow}>
            <View style={[styles.compareFeatureCell, { width: featureWidth }]}>
              <Text style={styles.compareFeatureText}>{row.feature}</Text>
            </View>
            {COMPARE_COLUMNS.map((column) => {
              const value = row[column.key];
              const isPerDiem = column.key === 'perdiem';
              return (
                <View key={column.key} style={[styles.compareCell, { width: columnWidth }]}>
                  {typeof value === 'boolean' ? (
                    <Ionicons
                      name={value ? 'checkmark' : 'remove'}
                      size={18}
                      color={value ? COLORS.green : 'rgba(242,244,239,0.28)'}
                    />
                  ) : (
                    <Text style={[styles.compareValue, isPerDiem ? { color: COLORS.green } : null]}>
                      {value}
                    </Text>
                  )}
                </View>
              );
            })}
          </View>
        ))}
      </View>
    </ScrollView>
  );
}

function FaqList({ expandedIndex, setExpandedIndex }: {
  expandedIndex: number;
  setExpandedIndex: (index: number) => void;
}) {
  return (
    <View style={styles.faqList}>
      {FAQS.map((faq, index) => {
        const isOpen = expandedIndex === index;
        return (
          <Pressable
            key={faq.question}
            accessibilityRole="button"
            accessibilityState={{ expanded: isOpen }}
            onPress={() => setExpandedIndex(isOpen ? -1 : index)}
            style={styles.faqItem}
          >
            <View style={styles.faqQuestionRow}>
              <Text style={styles.faqQuestion}>{faq.question}</Text>
              <View style={[styles.faqPlus, isOpen ? { backgroundColor: COLORS.green } : null]}>
                <Ionicons
                  name={isOpen ? 'close' : 'add'}
                  size={18}
                  color={isOpen ? '#06180D' : COLORS.ink}
                />
              </View>
            </View>
            {isOpen ? <Text style={styles.faqAnswer}>{faq.answer}</Text> : null}
          </Pressable>
        );
      })}
    </View>
  );
}

export default function NewLandingPage() {
  const router = useRouter();
  const pathname = usePathname();
  const queryParams = useLocalSearchParams<{ demo?: string }>();
  const { width, height } = useWindowDimensions();
  const isMobile = width < 760;
  const isTablet = width < 1024;
  const scrollRef = useRef<ScrollView>(null);
  const sectionPositionsRef = useRef<Record<SectionId, number>>({
    top: 0,
    daily: 0,
    connect: 0,
    plan: 0,
    scenarios: 0,
    phone: 0,
    chatgpt: 0,
    compare: 0,
    faq: 0,
    start: 0,
  });
  const [activeSection, setActiveSection] = useState<SectionId>('top');
  const [seenSections, setSeenSections] = useState<Partial<Record<SectionId, boolean>>>({ top: true });
  const [expandedFaq, setExpandedFaq] = useState(0);
  const [showDemoModal, setShowDemoModal] = useState(false);
  const [launchingDemoKey, setLaunchingDemoKey] = useState<DemoAccountKey | null>(null);
  const landingPageSlug = 'home';
  const landingPageTitle = 'Per Diem landing page';
  const placementPrefix = 'home';

  useEffect(() => {
    if (queryParams.demo === '1') {
      setShowDemoModal(true);
    }
  }, [queryParams.demo]);

  useEffect(() => {
    trackMarketingPageViewed({
      route: pathname,
      pageType: 'landing',
      pageSlug: landingPageSlug,
      title: landingPageTitle,
    });
  }, [landingPageSlug, landingPageTitle, pathname]);

  const markVisibleFromScroll = useCallback((scrollY: number) => {
    let nextActive: SectionId = 'top';
    const nextSeen: Partial<Record<SectionId, boolean>> = {};

    for (const id of SECTION_IDS) {
      const y = sectionPositionsRef.current[id] ?? 0;
      if (y <= scrollY + height * 0.46) nextActive = id;
      if (y <= scrollY + height * 0.82) nextSeen[id] = true;
    }

    setActiveSection((current) => (current === nextActive ? current : nextActive));
    setSeenSections((current) => {
      let changed = false;
      const merged = { ...current };
      for (const id of SECTION_IDS) {
        if (nextSeen[id] && !merged[id]) {
          merged[id] = true;
          changed = true;
        }
      }
      return changed ? merged : current;
    });
  }, [height]);

  const handleScroll = useCallback((event: NativeSyntheticEvent<NativeScrollEvent>) => {
    markVisibleFromScroll(event.nativeEvent.contentOffset.y);
  }, [markVisibleFromScroll]);

  const registerSection = useCallback((id: SectionId) => (event: LayoutChangeEvent) => {
    sectionPositionsRef.current[id] = event.nativeEvent.layout.y;
    if (id === 'top') {
      setSeenSections((current) => ({ ...current, top: true }));
    }
  }, []);

  const scrollToSection = useCallback((id: SectionId) => {
    const y = sectionPositionsRef.current[id] ?? 0;
    scrollRef.current?.scrollTo({ y: Math.max(0, y - (isMobile ? 72 : 0)), animated: true });
  }, [isMobile]);

  const handleDemoCta = useCallback((placement: string) => {
    trackMarketingCtaClicked({
      route: pathname,
      pageType: 'landing',
      pageSlug: landingPageSlug,
      ctaId: 'try-demo-profiles',
      destination: 'demo_profiles',
      placement,
    });

    void setPendingMarketingAttribution({
      source: 'landing_page',
      pageType: 'landing',
      pageSlug: landingPageSlug,
      ctaId: 'try-demo-profiles',
      destination: 'demo_profiles',
      placement,
    });

    setShowDemoModal(true);
  }, [landingPageSlug, pathname]);

  const handleSignupCta = useCallback((
    placement: string,
    options?: { ctaId?: string; promoCode?: string },
  ) => {
    const ctaId = options?.ctaId ?? 'create-account';

    trackMarketingCtaClicked({
      route: pathname,
      pageType: 'landing',
      pageSlug: landingPageSlug,
      ctaId,
      destination: 'signup',
      placement,
    });

    void setPendingMarketingAttribution({
      source: 'landing_page',
      pageType: 'landing',
      pageSlug: landingPageSlug,
      ctaId,
      destination: 'signup',
      placement,
    });

    const params = new URLSearchParams({ mode: 'signup' });
    if (options?.promoCode) params.set('promo', options.promoCode);

    const hostname = typeof globalThis.location?.hostname === 'string'
      ? globalThis.location.hostname
      : '';
    if (Platform.OS === 'web' && LOOPBACK_HOSTS.has(hostname)) {
      params.set('regular', '1');
    }

    router.push(`/(auth)/login?${params.toString()}` as Href);
  }, [landingPageSlug, pathname, router]);

  const handleLoginCta = useCallback((placement: string) => {
    trackMarketingCtaClicked({
      route: pathname,
      pageType: 'landing',
      pageSlug: landingPageSlug,
      ctaId: 'sign-in',
      destination: 'signin',
      placement,
    });

    void setPendingMarketingAttribution({
      source: 'landing_page',
      pageType: 'landing',
      pageSlug: landingPageSlug,
      ctaId: 'sign-in',
      destination: 'signin',
      placement,
    });

    router.push(LOGIN_ROUTE);
  }, [landingPageSlug, pathname, router]);

  const handleChatGptAppCta = useCallback((placement: string) => {
    trackMarketingCtaClicked({
      route: pathname,
      pageType: 'landing',
      pageSlug: landingPageSlug,
      ctaId: 'open-chatgpt-app',
      destination: 'chatgpt_app',
      placement,
    });

    openExternalUrl(CHATGPT_APP_URL);
  }, [landingPageSlug, pathname]);

  const handleMobileStoreCta = useCallback((store: keyof typeof MOBILE_APP_LINKS, placement: string) => {
    trackMarketingCtaClicked({
      route: pathname,
      pageType: 'landing',
      pageSlug: landingPageSlug,
      ctaId: store === 'ios' ? 'open-app-store' : 'open-google-play',
      destination: store === 'ios' ? 'app_store' : 'google_play',
      placement,
    });

    openExternalUrl(MOBILE_APP_LINKS[store]);
  }, [landingPageSlug, pathname]);

  const handleLaunchDemo = useCallback(async (accountKey: DemoAccountKey) => {
    setLaunchingDemoKey(accountKey);
    try {
      await signInDemoAccount(accountKey);
      setShowDemoModal(false);
      router.replace('/(app)');
    } finally {
      setLaunchingDemoKey(null);
    }
  }, [router]);

  const handleCloseDemoModal = useCallback(() => {
    setShowDemoModal(false);
    if (queryParams.demo === '1') {
      router.replace(pathname as Href);
    }
  }, [queryParams.demo, pathname, router]);

  const isCompactHero = isMobile && height < 760;
  const heroFontSize = isMobile ? (isCompactHero ? 42 : 50) : isTablet ? 68 : 76;
  const sectionHeadingSize = isMobile ? 42 : isTablet ? 64 : 78;
  const showNavLogin = width >= 360;
  const heroTopPadding = SHOW_SERVICE_OUTAGE_BANNER
    ? 204
    : (isMobile ? (isCompactHero ? 92 : 104) : 132);
  const heroMinHeight = isMobile
    ? Math.max(height, isCompactHero ? 780 : 900)
    : isTablet
      ? Math.max(height, 1180)
      : Math.max(height, 900);
  const heroLayoutStyle = useMemo<ViewStyle>(() => ({
    flexDirection: isTablet ? 'column' : 'row',
    gap: isCompactHero ? 18 : isMobile ? 30 : isTablet ? 52 : 74,
  }), [isCompactHero, isMobile, isTablet]);
  const splitStyle = useMemo<ViewStyle>(() => ({
    flexDirection: isTablet ? 'column' : 'row',
    gap: isMobile ? 44 : 76,
  }), [isMobile, isTablet]);
  const reversedSplitStyle = useMemo<ViewStyle>(() => ({
    flexDirection: isTablet ? 'column' : 'row-reverse',
    gap: isMobile ? 44 : 76,
  }), [isMobile, isTablet]);
  const stackedItemStyle = isTablet ? styles.stackedSplitItem : null;

  if (Platform.OS !== 'web') {
    return <Redirect href={'/(app)' as Href} />;
  }

  return (
    <>
      <WebHead><title>Per Diem - FIRE Planning and Daily Spending App</title></WebHead>

      <View
        style={[
          styles.fixedNav,
          isMobile ? styles.fixedNavMobile : null,
          webStyle({
            background: 'rgba(10,14,11,0.76)',
            backdropFilter: 'blur(18px)',
          }),
        ]}
      >
        <Pressable onPress={() => scrollToSection('top')} style={styles.brandLockup}>
          <LeafMark size={isMobile ? 32 : 36} />
          <View>
            <Text style={styles.brandName}>Per Diem</Text>
            <Text style={styles.brandTag}>Every day matters</Text>
          </View>
        </Pressable>

        <View style={styles.navActions}>
          {showNavLogin ? (
            <Pressable onPress={() => handleLoginCta(`${placementPrefix}-nav`)} style={styles.loginButton}>
              <Text numberOfLines={1} style={styles.loginText}>Log in</Text>
            </Pressable>
          ) : null}
          <PrimaryButton
            label={isMobile ? 'Start' : 'Start 14 days free'}
            onPress={() => handleSignupCta(`${placementPrefix}-nav`, {
              ctaId: 'start-free-trial',
            })}
          />
        </View>
      </View>

      <ServiceOutageBanner isMobile={isMobile} />

      {!isMobile && width >= 1000 ? (
        <View style={[styles.progressRail, { pointerEvents: 'none' }]}>
          {SECTION_IDS.map((id) => (
            <View
              key={id}
              style={[
                styles.progressDot,
                activeSection === id ? styles.progressDotActive : null,
              ]}
            />
          ))}
        </View>
      ) : null}

      {!isMobile && width >= 1000 ? (
        <Pressable
          onPress={() => scrollToSection('connect')}
          accessibilityLabel="Scroll down"
          style={[
            styles.scrollHint,
            webStyle({
              animation: 'pdScrollHint 2.4s ease-in-out infinite',
              opacity: activeSection === 'top' ? undefined : 0,
              pointerEvents: activeSection === 'top' ? 'auto' : 'none',
              transition: 'opacity 400ms ease',
            }),
          ]}
        >
          <Ionicons name="chevron-down" size={28} color={COLORS.ink} />
        </Pressable>
      ) : null}

      <main style={styles.mainLandmark as CSSProperties}>
        <ScrollView
          ref={scrollRef}
          style={styles.page}
          contentContainerStyle={styles.pageContent}
          onLayout={() => markVisibleFromScroll(0)}
          onScroll={handleScroll}
          scrollEventThrottle={16}
        >
        <View
          onLayout={registerSection('top')}
          style={[
            styles.section,
            sectionBackground('top'),
            {
              minHeight: heroMinHeight,
              paddingTop: heroTopPadding,
              paddingBottom: isMobile ? 0 : 72,
            },
          ]}
        >
          <View style={[styles.sectionInner, styles.heroInner, heroLayoutStyle]}>
            <View style={[
              styles.heroCopy,
              isTablet ? styles.heroCopyStacked : null,
              isCompactHero ? styles.heroCopyCompact : null,
            ]}>
              <View style={revealStyle(Boolean(seenSections.top))}>
                <Eyebrow align={isTablet ? 'center' : 'start'}>Built for the FIRE community</Eyebrow>
              </View>
              <Text
                accessibilityRole="header"
                style={[
                  displayTextStyle(heroFontSize, isMobile),
                  styles.heroTitle,
                  isTablet ? styles.heroTitleStacked : null,
                  revealStyle(Boolean(seenSections.top), 80) as TextStyle,
                ]}
              >
                Know what you can{'\n'}spend today — and{'\n'}still retire early.
              </Text>
              <Text
                style={[
                  styles.heroSub,
                  isMobile ? styles.heroSubMobile : null,
                  isCompactHero ? styles.heroSubCompact : null,
                  isTablet ? styles.heroSubStacked : null,
                  revealStyle(Boolean(seenSections.top), 160) as TextStyle,
                ]}
              >
                Per Diem turns your accounts, budget, and portfolio into one daily
                number that keeps your FIRE plan on track.
              </Text>
              <View style={[
                styles.heroCtas,
                isCompactHero ? styles.heroCtasCompact : null,
                !isTablet ? styles.heroCtasLeft : null,
                revealStyle(Boolean(seenSections.top), 240),
              ]}>
                <PrimaryButton
                  label="Start my 14-day free trial"
                  onPress={() => handleSignupCta(`${placementPrefix}-hero`, {
                    ctaId: 'start-free-trial',
                  })}
                />
                <GhostButton
                  label="Explore a demo"
                  onPress={() => handleDemoCta(`${placementPrefix}-hero`)}
                />
              </View>
              <View style={[
                styles.heroMeta,
                isTablet ? styles.heroMetaStacked : null,
                revealStyle(Boolean(seenSections.top), 320),
              ]}>
                <Text style={[
                  monoCapsStyle(),
                  styles.heroMetaText,
                  isCompactHero ? styles.heroMetaTextCompact : null,
                ]}>
                  Read-only account connections · $0 today · Cancel anytime
                </Text>
              </View>
            </View>
            <View
              style={[
                styles.heroVisual,
                isTablet ? styles.heroVisualStacked : null,
                revealStyle(Boolean(seenSections.top), 180),
              ]}
            >
              <HeroPhonePreview isMobile={isMobile} isStacked={isTablet} />
            </View>
          </View>
        </View>

        <View
          onLayout={registerSection('connect')}
          style={[styles.section, sectionBackground('connect'), { minHeight: Math.max(height, 900) }]}
        >
          <SectionNumber label="02 - Connect" hidden={isMobile} />
          <View style={[styles.sectionInner, reversedSplitStyle]}>
            <View style={[styles.copyColumn, stackedItemStyle, revealStyle(Boolean(seenSections.connect), 160)]}>
              <Eyebrow>One household model</Eyebrow>
              <Text style={displayTextStyle(sectionHeadingSize, isMobile)}>Accounts, budgets, and plans finally talk to each other.</Text>
              <Text style={styles.sectionSub}>
                Connect your accounts, watch Per Diem auto-infer your budget, then see your path
                to financial independence. Once your plan is finalized, use the iOS and Android
                apps to track your spending and help you stay on track.
              </Text>
              <BulletList
                items={[
                  'Synced transactions give Per Diem a first pass at your real spending patterns.',
                  'Account balances, inferred budget, and FIRE assumptions flow into one plan.',
                  'Not ready to connect your accounts yet? Try the demo profiles first.',
                ]}
              />
            </View>
            <View style={[styles.artColumn, stackedItemStyle, revealStyle(Boolean(seenSections.connect))]}>
              <AccountManifest isMobile={isMobile} />
            </View>
          </View>
        </View>

        <View
          onLayout={registerSection('daily')}
          style={[styles.section, sectionBackground('daily'), { minHeight: Math.max(height, 860) }]}
        >
          <SectionNumber label="03 - The number" hidden={isMobile} />
          <View style={[styles.sectionInner, splitStyle]}>
            <View style={[styles.copyColumn, stackedItemStyle, revealStyle(Boolean(seenSections.daily))]}>
              <Eyebrow>Your per diem</Eyebrow>
              <Text style={displayTextStyle(sectionHeadingSize, isMobile)}>A daily number for the decision in front of you.</Text>
              <Text style={styles.sectionSub}>
                Budgets tell you where money went. Your per diem tells you what today can hold,
                using the same household plan behind your FIRE projection.
              </Text>
              <BulletList
                items={[
                  'Built from income, fixed expenses, discretionary budget, and month-to-date spending.',
                  'Underspend and room rolls forward. Overspend and the remaining month adjusts.',
                  'On the first day of the month, the number starts clean instead of comparing against yesterday.',
                ]}
              />
            </View>
            <View style={[styles.artColumn, stackedItemStyle, revealStyle(Boolean(seenSections.daily), 180)]}>
              <PerDiemPreviewCard isMobile={isMobile} />
            </View>
          </View>
        </View>

        <View
          onLayout={registerSection('plan')}
          style={[styles.section, sectionBackground('plan'), { minHeight: Math.max(height, 860) }]}
        >
          <SectionNumber label="04 - Plan" hidden={isMobile} />
          <View style={[styles.sectionInner, splitStyle]}>
            <View style={[styles.copyColumn, stackedItemStyle, revealStyle(Boolean(seenSections.plan))]}>
              <Eyebrow>FIRE plan</Eyebrow>
              <Text style={displayTextStyle(sectionHeadingSize, isMobile)}>See how today affects the plan.</Text>
              <Text style={styles.sectionSub}>
                The retirement model should not live in a separate calculator. Per Diem keeps
                spending, income, taxes, life events, and FIRE projections in one workflow.
              </Text>
              <BulletList
                items={[
                  'Monte Carlo projections use household-specific income, expenses, assets, and assumptions.',
                  'Plan forks let you test big life changes without overwriting your baseline.',
                  'Tax estimates stay framed as planning inputs, not filing advice.',
                ]}
              />
            </View>
            <View style={[styles.artColumn, stackedItemStyle, revealStyle(Boolean(seenSections.plan), 180)]}>
              <FirePlanPreviewCard isMobile={isMobile} />
            </View>
          </View>
        </View>

        <View
          onLayout={registerSection('scenarios')}
          style={[styles.section, sectionBackground('scenarios'), { minHeight: Math.max(height, 860) }]}
        >
          <SectionNumber label="05 - Scenarios" hidden={isMobile} />
          <View style={[styles.sectionInner, reversedSplitStyle]}>
            <View style={[styles.copyColumn, stackedItemStyle, revealStyle(Boolean(seenSections.scenarios), 160)]}>
              <Eyebrow tone="amber">Scenario planning</Eyebrow>
              <Text style={displayTextStyle(sectionHeadingSize, isMobile)}>Try the what-ifs without rewriting the plan.</Text>
              <Text style={styles.sectionSub}>
                Save scenarios for spending changes, income shifts, market moves, and life
                events, then compare the FI result against your baseline.
              </Text>
              <BulletList
                items={[
                  'Guided starters cover budget, income, market, purchase, and windfall changes.',
                  'Deeper edits can model state moves, job pauses, added income, holding prices, and simulated life events.',
                  'Your default FIRE plan stays unchanged while you explore.',
                ]}
              />
            </View>
            <View style={[styles.artColumn, stackedItemStyle, revealStyle(Boolean(seenSections.scenarios))]}>
              <ScenarioPlanningCard isMobile={isMobile} />
            </View>
          </View>
        </View>

        <View
          onLayout={registerSection('phone')}
          style={[styles.section, sectionBackground('phone'), { minHeight: Math.max(height, 900) }]}
        >
          <SectionNumber label="06 - Phone" hidden={isMobile} />
          <View style={[styles.sectionInner, reversedSplitStyle]}>
            <View style={[styles.copyColumn, stackedItemStyle, revealStyle(Boolean(seenSections.phone), 160)]}>
              <Eyebrow tone="amber">Mobile check-ins</Eyebrow>
              <Text style={displayTextStyle(sectionHeadingSize, isMobile)}>Check in from your phone.</Text>
              <Text style={styles.sectionSub}>
                Set up the household plan on web, then use iOS or Android for quick daily checks.
              </Text>
              <BulletList
                items={[
                  'See today\'s per diem from the same budget and account context.',
                  'Review balances, recent spending, and the FIRE plan between deeper planning sessions.',
                  'Use web when you want more room to connect accounts and adjust the model.',
                ]}
              />
              <View style={styles.storeBadgeRow}>
                <Pressable
                  accessibilityRole="link"
                  accessibilityLabel="Download Per Diem on the App Store"
                  onPress={() => handleMobileStoreCta('ios', `${placementPrefix}-phone`)}
                  style={styles.storeBadgeButton}
                >
                  <Image source={STORE_BADGES.ios} resizeMode="contain" style={styles.appStoreBadge} />
                </Pressable>
                <Pressable
                  accessibilityRole="link"
                  accessibilityLabel="Get Per Diem on Google Play"
                  onPress={() => handleMobileStoreCta('android', `${placementPrefix}-phone`)}
                  style={styles.storeBadgeButton}
                >
                  <Image source={STORE_BADGES.android} resizeMode="contain" style={styles.playStoreBadge} />
                </Pressable>
              </View>
            </View>
            <View style={[styles.artColumn, stackedItemStyle, revealStyle(Boolean(seenSections.phone))]}>
              <PhoneStack isMobile={isMobile} />
            </View>
          </View>
        </View>

        <View
          onLayout={registerSection('chatgpt')}
          style={[styles.section, sectionBackground('chatgpt'), { minHeight: Math.max(height, 820) }]}
        >
          <SectionNumber label="07 - ChatGPT" hidden={isMobile} />
          <View style={[styles.sectionInner, splitStyle]}>
            <View style={[styles.copyColumn, stackedItemStyle, revealStyle(Boolean(seenSections.chatgpt))]}>
              <Eyebrow>Chat with your finances</Eyebrow>
              <Text style={displayTextStyle(sectionHeadingSize, isMobile)}>Ask your plan questions.</Text>
              <Text style={styles.sectionSub}>
                Authorize Per Diem in ChatGPT to ask questions against your budget,
                transactions, balances, cash flow, and FIRE plan.
              </Text>
              <BulletList
                items={[
                  'Ask what changed, which categories are off plan, or how a scenario affects your FIRE timeline.',
                  'Per Diem shares authorized context only; the ChatGPT app cannot move money or edit records.',
                  'Watch the walkthrough to see what it can answer today.',
                ]}
              />
              <View style={styles.finalLinksLeft}>
                <Pressable onPress={() => handleChatGptAppCta(`${placementPrefix}-chatgpt-section`)}>
                  <Text style={styles.inlineLink}>Open in ChatGPT App Directory</Text>
                </Pressable>
                <Pressable onPress={() => router.push(AI_CONNECTORS_ROUTE)}>
                  <Text style={styles.inlineLink}>How the connector works</Text>
                </Pressable>
              </View>
            </View>
            <View style={[styles.artColumn, stackedItemStyle, revealStyle(Boolean(seenSections.chatgpt), 180)]}>
              <ChatGptVideoCard isMobile={isMobile} />
            </View>
          </View>
        </View>

        <View
          onLayout={registerSection('compare')}
          style={[styles.section, sectionBackground('compare'), { minHeight: Math.max(height, 820) }]}
        >
          <SectionNumber label="08 - Compare" hidden={isMobile} />
          <View style={[styles.sectionInner, styles.centerSection]}>
            <View style={[styles.centerCopy, revealStyle(Boolean(seenSections.compare))]}>
              <Text style={[displayTextStyle(sectionHeadingSize, isMobile), { textAlign: 'center' }]}>
                Built for the overlap other tools miss.
              </Text>
              <Text style={[styles.sectionSub, { textAlign: 'center' }]}>
                Per Diem is for FIRE-minded households that want daily budgeting and
                long-range planning to stay connected.
              </Text>
            </View>
            <View style={[styles.compareWrap, revealStyle(Boolean(seenSections.compare), 180)]}>
              <ComparisonTable isMobile={isMobile} />
            </View>
          </View>
        </View>

        <View
          onLayout={registerSection('faq')}
          style={[styles.section, sectionBackground('faq'), { minHeight: Math.max(height, 840) }]}
        >
          <SectionNumber label="09 - FAQ" hidden={isMobile} />
          <View style={[styles.sectionInner, splitStyle]}>
            <View style={[styles.copyColumn, stackedItemStyle, revealStyle(Boolean(seenSections.faq))]}>
              <Eyebrow>Answers</Eyebrow>
              <Text style={displayTextStyle(sectionHeadingSize, isMobile)}>Clear answers before you connect.</Text>
              <Text style={styles.sectionSub}>
                The basics on the daily number, demo profiles, ChatGPT access, and account security.
              </Text>
            </View>
            <View style={[styles.faqColumn, stackedItemStyle, revealStyle(Boolean(seenSections.faq), 160)]}>
              <FaqList expandedIndex={expandedFaq} setExpandedIndex={setExpandedFaq} />
            </View>
          </View>
        </View>

        <View
          onLayout={registerSection('start')}
          style={[styles.section, sectionBackground('start'), { minHeight: Math.max(height, 760) }]}
        >
          <View style={[styles.sectionInner, styles.finalInner, revealStyle(Boolean(seenSections.start))]}>
            <Eyebrow align="center">Start today</Eyebrow>
            <Text style={[displayTextStyle(isMobile ? 48 : 82, isMobile), { textAlign: 'center' }]}>
              Per Diem.{'\n'}Because every day matters.
            </Text>
            <Text style={[styles.sectionSub, { textAlign: 'center', maxWidth: 680 }]}>
              Know what today can handle. See how the future changes. Stop rebuilding
              the same plan across tools.
            </Text>
            <View style={styles.heroCtas}>
              <PrimaryButton
                tone="green"
                label="Start 14 days free"
                onPress={() => handleSignupCta(`${placementPrefix}-final`, {
                  ctaId: 'start-free-trial',
                })}
              />
              <GhostButton label="Try a demo profile first" onPress={() => handleDemoCta(`${placementPrefix}-final`)} />
            </View>
          </View>
        </View>

        <View style={styles.footer}>
          <View style={styles.footerLinks}>
            <Pressable onPress={() => router.push(GUIDES_ROUTE)}><Text style={styles.footerLink}>Guides</Text></Pressable>
            <Pressable onPress={() => router.push(COMPARE_ROUTE)}><Text style={styles.footerLink}>Compare</Text></Pressable>
            <Pressable onPress={() => router.push(SUPPORT_ROUTE)}><Text style={styles.footerLink}>Support</Text></Pressable>
            <Pressable onPress={() => router.push(LEGAL_LINKS.privacy)}><Text style={styles.footerLink}>Privacy</Text></Pressable>
            <Pressable onPress={() => router.push(LEGAL_LINKS.security)}><Text style={styles.footerLink}>Security</Text></Pressable>
            <Pressable onPress={() => router.push(LEGAL_LINKS.terms)}><Text style={styles.footerLink}>Terms</Text></Pressable>
            <Pressable onPress={() => openExternalUrl(X_URL)}><Text style={styles.footerLink}>@perdiem_app</Text></Pressable>
          </View>
        </View>
        </ScrollView>
      </main>

      <DemoProfilePickerModal
        visible={showDemoModal}
        accounts={DEMO_ACCOUNTS}
        launchingDemoKey={launchingDemoKey}
        onClose={handleCloseDemoModal}
        onLaunchDemo={handleLaunchDemo}
      />
    </>
  );
}

const styles = {
  mainLandmark: {
    display: 'flex',
    flex: 1,
    minHeight: '100vh',
  },
  page: {
    flex: 1,
    backgroundColor: COLORS.bg,
  } satisfies ViewStyle,
  pageContent: {
    backgroundColor: COLORS.bg,
  } satisfies ViewStyle,
  fixedNav: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    zIndex: 50,
    minHeight: 86,
    paddingHorizontal: 38,
    paddingVertical: 18,
    borderBottomWidth: 1,
    borderBottomColor: COLORS.line,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 18,
  } as unknown as ViewStyle,
  fixedNavMobile: {
    minHeight: 80,
    paddingHorizontal: 18,
    paddingVertical: 14,
    gap: 10,
  } satisfies ViewStyle,
  serviceOutageBanner: {
    position: 'fixed',
    top: 86,
    left: 0,
    right: 0,
    zIndex: 49,
    paddingHorizontal: 22,
    paddingVertical: 10,
    borderBottomWidth: 1,
    borderBottomColor: 'rgba(6,24,13,0.24)',
    backgroundColor: COLORS.amber,
    ...webStyle({
      boxShadow: '0 12px 30px -22px rgba(0,0,0,0.72)',
    }),
  } as unknown as ViewStyle,
  serviceOutageBannerMobile: {
    top: 80,
    paddingHorizontal: 14,
    paddingVertical: 10,
  } as unknown as ViewStyle,
  serviceOutageInner: {
    width: '100%',
    maxWidth: 1180,
    marginHorizontal: 'auto',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 10,
  } satisfies ViewStyle,
  serviceOutageInnerMobile: {
    alignItems: 'flex-start',
  } satisfies ViewStyle,
  serviceOutageText: {
    flexShrink: 1,
    color: '#06180D',
    fontSize: 13,
    fontWeight: '600',
    lineHeight: 20,
    textAlign: 'center',
  } satisfies TextStyle,
  serviceOutageTextMobile: {
    fontSize: 12,
    lineHeight: 18,
    textAlign: 'left',
  } satisfies TextStyle,
  serviceOutageStrong: {
    color: '#06180D',
    fontWeight: '800',
  } satisfies TextStyle,
  brandLockup: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    minWidth: 0,
  } satisfies ViewStyle,
  leafMark: {
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: COLORS.green,
  } satisfies ViewStyle,
  brandName: {
    color: COLORS.ink,
    fontSize: 16,
    fontWeight: '700',
    letterSpacing: 0,
    lineHeight: 18,
  } satisfies TextStyle,
  brandTag: {
    color: COLORS.ink3,
    fontSize: 12,
    lineHeight: 15,
  } satisfies TextStyle,
  scrollHint: {
    ...webStyle({
      position: 'fixed',
      left: '50%',
      bottom: 28,
      zIndex: 50,
    }),
    width: 48,
    height: 48,
    borderRadius: 999,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(242,244,239,0.06)',
    borderWidth: 1,
    borderColor: 'rgba(242,244,239,0.18)',
  } as unknown as ViewStyle,
  navActions: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: 10,
    flexShrink: 0,
  } satisfies ViewStyle,
  loginButton: {
    paddingHorizontal: 8,
    paddingVertical: 10,
  } satisfies ViewStyle,
  loginText: {
    color: COLORS.ink2,
    fontFamily: MONO_FONT,
    fontSize: 11,
    letterSpacing: 1.2,
    textTransform: 'uppercase',
  } satisfies TextStyle,
  button: {
    minHeight: 46,
    borderRadius: 999,
    borderWidth: 1,
    paddingHorizontal: 18,
    paddingVertical: 11,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
  } satisfies ViewStyle,
  buttonLabel: {
    fontFamily: MONO_FONT_MEDIUM,
    fontSize: 12,
    letterSpacing: 1,
    textTransform: 'uppercase',
  } satisfies TextStyle,
  ghostButton: {
    backgroundColor: 'rgba(242,244,239,0.045)',
    borderColor: COLORS.line,
  } satisfies ViewStyle,
  progressRail: {
    position: 'fixed',
    right: 24,
    top: '39%',
    zIndex: 40,
    gap: 10,
    alignItems: 'center',
  } as unknown as ViewStyle,
  progressDot: {
    width: 2,
    height: 20,
    borderRadius: 999,
    backgroundColor: 'rgba(242,244,239,0.18)',
  } satisfies ViewStyle,
  progressDotActive: {
    backgroundColor: COLORS.green,
    height: 28,
  } satisfies ViewStyle,
  section: {
    position: 'relative',
    overflow: 'hidden',
    paddingHorizontal: 20,
    paddingVertical: 112,
    alignItems: 'center',
    justifyContent: 'center',
  } satisfies ViewStyle,
  sectionInner: {
    width: '100%',
    maxWidth: 1180,
    marginHorizontal: 'auto',
    alignItems: 'center',
    justifyContent: 'center',
  } satisfies ViewStyle,
  heroInner: {
    justifyContent: 'space-between',
  } satisfies ViewStyle,
  heroCopy: {
    flex: 1,
    width: '100%',
    maxWidth: 650,
    alignItems: 'flex-start',
    gap: 22,
  } satisfies ViewStyle,
  heroCopyStacked: {
    maxWidth: 720,
    alignItems: 'center',
  } satisfies ViewStyle,
  heroCopyCompact: {
    gap: 16,
  } satisfies ViewStyle,
  heroTitle: {
    maxWidth: 650,
    textAlign: 'left',
  } satisfies TextStyle,
  heroTitleStacked: {
    textAlign: 'center',
  } satisfies TextStyle,
  heroSub: {
    color: COLORS.ink2,
    fontSize: 19,
    lineHeight: 30,
    maxWidth: 610,
    textAlign: 'left',
  } satisfies TextStyle,
  heroSubMobile: {
    fontSize: 17,
    lineHeight: 27,
  } satisfies TextStyle,
  heroSubCompact: {
    fontSize: 16,
    lineHeight: 24,
  } satisfies TextStyle,
  heroSubStacked: {
    textAlign: 'center',
  } satisfies TextStyle,
  heroCtas: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 14,
  } satisfies ViewStyle,
  heroCtasLeft: {
    justifyContent: 'flex-start',
  } satisfies ViewStyle,
  heroCtasCompact: {
    gap: 10,
  } satisfies ViewStyle,
  heroMeta: {
    marginTop: 4,
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'flex-start',
  } satisfies ViewStyle,
  heroMetaStacked: {
    alignItems: 'center',
  } satisfies ViewStyle,
  heroMetaText: {
    lineHeight: 18,
    textAlign: 'center',
  } satisfies TextStyle,
  heroMetaTextCompact: {
    fontSize: 9,
    letterSpacing: 1.3,
    lineHeight: 16,
  } satisfies TextStyle,
  heroVisual: {
    flex: 1,
    width: '100%',
    maxWidth: 430,
    alignItems: 'center',
    justifyContent: 'flex-start',
  } satisfies ViewStyle,
  heroVisualStacked: {
    flexGrow: 0,
    flexShrink: 0,
    flexBasis: 'auto',
    maxWidth: 460,
  } satisfies ViewStyle,
  heroPhoneStage: {
    position: 'relative',
    width: '100%',
    maxWidth: 460,
    overflow: 'hidden',
    alignItems: 'center',
    justifyContent: 'flex-start',
  } satisfies ViewStyle,
  heroPhoneHalo: {
    position: 'absolute',
    top: 30,
    left: '50%',
    width: 520,
    height: 520,
    marginLeft: -260,
    borderRadius: 999,
  } satisfies ViewStyle,
  heroPhone: {
    marginTop: 8,
  } satisfies ViewStyle,
  eyebrow: {
    alignSelf: 'flex-start',
    minHeight: 30,
    borderWidth: 1,
    borderRadius: 999,
    paddingHorizontal: 13,
    paddingVertical: 7,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  } satisfies ViewStyle,
  eyebrowCenter: {
    alignSelf: 'center',
  } satisfies ViewStyle,
  eyebrowDot: {
    width: 6,
    height: 6,
    borderRadius: 999,
  } satisfies ViewStyle,
  sectionNumber: {
    position: 'absolute',
    top: 112,
    left: 40,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
  } satisfies ViewStyle,
  sectionRule: {
    width: 24,
    height: 1,
    backgroundColor: COLORS.ink3,
  } satisfies ViewStyle,
  copyColumn: {
    flex: 1,
    width: '100%',
    maxWidth: 540,
    gap: 24,
    alignItems: 'flex-start',
  } satisfies ViewStyle,
  artColumn: {
    flex: 1,
    width: '100%',
    alignItems: 'center',
    justifyContent: 'center',
  } satisfies ViewStyle,
  stackedSplitItem: {
    flex: 0,
    width: '100%',
    ...webStyle({ flexBasis: 'auto' }),
  } satisfies ViewStyle,
  sectionSub: {
    color: COLORS.ink2,
    fontSize: 18,
    lineHeight: 29,
    maxWidth: 580,
  } satisfies TextStyle,
  bulletList: {
    gap: 14,
    marginTop: 2,
  } satisfies ViewStyle,
  bulletRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 12,
  } satisfies ViewStyle,
  bulletDot: {
    width: 6,
    height: 6,
    marginTop: 10,
    borderRadius: 999,
    backgroundColor: COLORS.green,
  } satisfies ViewStyle,
  bulletText: {
    flex: 1,
    color: COLORS.ink2,
    fontSize: 15,
    lineHeight: 24,
  } satisfies TextStyle,
  perDiemCard: {
    width: '100%',
    borderWidth: 1,
    borderColor: 'rgba(60,183,120,0.30)',
    borderRadius: 22,
  } satisfies ViewStyle,
  perDiemLabelRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 14,
  } satisfies ViewStyle,
  livePill: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 7,
  } satisfies ViewStyle,
  liveDot: {
    width: 6,
    height: 6,
    borderRadius: 999,
    backgroundColor: COLORS.green,
  } satisfies ViewStyle,
  bigMoney: {
    color: COLORS.ink,
    marginTop: 18,
    letterSpacing: 0,
    width: '100%',
  } satisfies TextStyle,
  greenMono: {
    color: COLORS.green,
    fontFamily: MONO_FONT,
    fontSize: 12,
    letterSpacing: 1,
  } satisfies TextStyle,
  progressTrack: {
    marginTop: 24,
    height: 6,
    borderRadius: 999,
    backgroundColor: 'rgba(242,244,239,0.10)',
    overflow: 'hidden',
  } satisfies ViewStyle,
  progressFill: {
    height: '100%',
    borderRadius: 999,
    backgroundColor: COLORS.green,
  } satisfies ViewStyle,
  accountManifest: {
    width: '100%',
    gap: 10,
  } satisfies ViewStyle,
  accountHead: {
    paddingHorizontal: 4,
    paddingBottom: 8,
    flexDirection: 'row',
    justifyContent: 'space-between',
    gap: 12,
  } satisfies ViewStyle,
  accountRow: {
    borderWidth: 1,
    borderColor: COLORS.line,
    borderRadius: 14,
    paddingHorizontal: 16,
    paddingVertical: 14,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 14,
  } satisfies ViewStyle,
  accountLogoFrame: {
    width: 38,
    height: 38,
    borderRadius: 10,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#ffffff',
    overflow: 'hidden',
  } satisfies ViewStyle,
  accountLogo: {
    width: 30,
    height: 30,
  } satisfies ViewStyle,
  accountMeta: {
    flex: 1,
    minWidth: 0,
    gap: 2,
  } satisfies ViewStyle,
  accountName: {
    color: COLORS.ink,
    fontSize: 15,
    fontWeight: '600',
  } satisfies TextStyle,
  accountValue: {
    alignItems: 'flex-end',
    gap: 3,
  } satisfies ViewStyle,
  accountMoney: {
    color: COLORS.ink,
    fontSize: 20,
  } satisfies TextStyle,
  aggregator: {
    marginTop: 18,
    alignItems: 'center',
    gap: 14,
  } satisfies ViewStyle,
  flowLines: {
    width: '88%',
    alignItems: 'center',
    gap: 8,
  } satisfies ViewStyle,
  flowLine: {
    height: 1,
    width: '100%',
    backgroundColor: 'rgba(60,183,120,0.34)',
  } satisfies ViewStyle,
  aggregatorCard: {
    borderWidth: 1,
    borderColor: 'rgba(60,183,120,0.34)',
    borderRadius: 14,
    paddingHorizontal: 18,
    paddingVertical: 14,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 13,
  } satisfies ViewStyle,
  aggregatorMoney: {
    color: COLORS.ink,
    fontSize: 28,
    marginTop: 2,
  } satisfies TextStyle,
  scenarioCard: {
    width: '100%',
    borderWidth: 1,
    borderColor: 'rgba(94,143,184,0.32)',
    borderRadius: 20,
    gap: 18,
  } satisfies ViewStyle,
  scenarioHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    gap: 14,
  } satisfies ViewStyle,
  scenarioBaseline: {
    borderWidth: 1,
    borderColor: 'rgba(242,244,239,0.12)',
    borderRadius: 16,
    paddingHorizontal: 18,
    paddingVertical: 16,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 16,
    backgroundColor: 'rgba(242,244,239,0.045)',
  } satisfies ViewStyle,
  scenarioBaselineCopy: {
    flex: 1,
    minWidth: 0,
    gap: 4,
  } satisfies ViewStyle,
  scenarioBaselineTitle: {
    color: COLORS.ink,
    fontSize: 24,
    fontWeight: '700',
    lineHeight: 30,
  } satisfies TextStyle,
  scenarioBaselineBadge: {
    borderRadius: 999,
    paddingHorizontal: 10,
    paddingVertical: 6,
    backgroundColor: 'rgba(60,183,120,0.22)',
  } satisfies ViewStyle,
  scenarioRows: {
    gap: 10,
  } satisfies ViewStyle,
  scenarioRow: {
    minHeight: 68,
    borderWidth: 1,
    borderColor: 'rgba(242,244,239,0.10)',
    borderRadius: 14,
    paddingHorizontal: 16,
    paddingVertical: 13,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 16,
  } satisfies ViewStyle,
  scenarioRowCopy: {
    flex: 1,
    minWidth: 0,
    gap: 3,
  } satisfies ViewStyle,
  scenarioName: {
    color: COLORS.ink,
    fontSize: 15,
    fontWeight: '700',
    lineHeight: 20,
  } satisfies TextStyle,
  scenarioOutcome: {
    alignItems: 'flex-end',
    minWidth: 92,
  } satisfies ViewStyle,
  scenarioYear: {
    color: COLORS.ink,
    fontFamily: MONO_FONT_MEDIUM,
    fontSize: 19,
    lineHeight: 24,
  } satisfies TextStyle,
  scenarioResult: {
    fontFamily: MONO_FONT,
    fontSize: 10,
    letterSpacing: 0.8,
    lineHeight: 14,
    textTransform: 'uppercase',
  } satisfies TextStyle,
  phoneStack: {
    flexDirection: 'row',
    justifyContent: 'center',
    minHeight: 560,
    paddingLeft: 10,
  } satisfies ViewStyle,
  phoneShell: {
    position: 'relative',
    borderWidth: 1,
    borderColor: 'rgba(242,244,239,0.14)',
    borderRadius: 36,
    padding: 10,
    backgroundColor: '#070A07',
    overflow: 'hidden',
  } satisfies ViewStyle,
  phoneNotch: {
    position: 'absolute',
    top: 16,
    left: '50%',
    marginLeft: -40,
    width: 80,
    height: 24,
    borderRadius: 999,
    backgroundColor: '#000000',
    zIndex: 2,
  } satisfies ViewStyle,
  phoneImage: {
    width: '100%',
    height: '100%',
    borderRadius: 28,
  } satisfies ViewStyle,
  storeBadgeRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 12,
    marginTop: 2,
  } satisfies ViewStyle,
  storeBadgeButton: {
    borderRadius: 6,
  } satisfies ViewStyle,
  appStoreBadge: {
    width: 120,
    height: 40,
  } satisfies ViewStyle,
  playStoreBadge: {
    width: 135,
    height: 40,
  } satisfies ViewStyle,
  chatGptVideoCard: {
    width: '100%',
    borderWidth: 1,
    borderColor: COLORS.line,
    borderRadius: 20,
    padding: 12,
    backgroundColor: 'rgba(242,244,239,0.045)',
  } satisfies ViewStyle,
  videoFrame: {
    width: '100%',
    aspectRatio: 16 / 9,
    borderRadius: 14,
    overflow: 'hidden',
    backgroundColor: '#050705',
  } satisfies ViewStyle,
  centerSection: {
    gap: 50,
  } satisfies ViewStyle,
  centerCopy: {
    alignItems: 'center',
    gap: 22,
    maxWidth: 760,
  } satisfies ViewStyle,
  compareWrap: {
    width: '100%',
    borderWidth: 1,
    borderColor: COLORS.line,
    borderRadius: 18,
    padding: 16,
    backgroundColor: 'rgba(242,244,239,0.035)',
  } satisfies ViewStyle,
  compareTable: {
    overflow: 'hidden',
  } satisfies ViewStyle,
  compareRow: {
    flexDirection: 'row',
    alignItems: 'stretch',
    borderBottomWidth: 1,
    borderBottomColor: 'rgba(242,244,239,0.07)',
  } satisfies ViewStyle,
  compareFeatureCell: {
    minHeight: 46,
    justifyContent: 'center',
    paddingHorizontal: 12,
    paddingVertical: 11,
  } satisfies ViewStyle,
  compareCell: {
    minHeight: 46,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 10,
    paddingVertical: 11,
  } satisfies ViewStyle,
  compareHead: {
    color: COLORS.ink2,
    fontSize: 13,
    fontWeight: '700',
    textAlign: 'center',
  } satisfies TextStyle,
  compareFeatureText: {
    color: COLORS.ink2,
    fontSize: 14,
    lineHeight: 20,
  } satisfies TextStyle,
  compareValue: {
    color: COLORS.ink2,
    fontFamily: MONO_FONT_MEDIUM,
    fontSize: 13,
  } satisfies TextStyle,
  faqColumn: {
    flex: 1.2,
    width: '100%',
  } satisfies ViewStyle,
  faqList: {
    borderTopWidth: 1,
    borderTopColor: COLORS.line,
  } satisfies ViewStyle,
  faqItem: {
    borderBottomWidth: 1,
    borderBottomColor: COLORS.line,
    paddingVertical: 22,
  } satisfies ViewStyle,
  faqQuestionRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 20,
  } satisfies ViewStyle,
  faqQuestion: {
    flex: 1,
    color: COLORS.ink,
    fontSize: 19,
    fontWeight: '600',
    lineHeight: 26,
  } satisfies TextStyle,
  faqPlus: {
    width: 30,
    height: 30,
    borderRadius: 999,
    borderWidth: 1,
    borderColor: COLORS.line,
    alignItems: 'center',
    justifyContent: 'center',
  } satisfies ViewStyle,
  faqAnswer: {
    color: COLORS.ink2,
    fontSize: 16,
    lineHeight: 26,
    marginTop: 14,
    maxWidth: 760,
  } satisfies TextStyle,
  finalInner: {
    alignItems: 'center',
    gap: 24,
  } satisfies ViewStyle,
  finalLinks: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'center',
    gap: 18,
  } satisfies ViewStyle,
  finalLinksLeft: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 20,
  } satisfies ViewStyle,
  inlineLink: {
    color: COLORS.green,
    fontSize: 14,
    fontWeight: '700',
  } satisfies TextStyle,
  footer: {
    paddingHorizontal: 28,
    paddingVertical: 34,
    borderTopWidth: 1,
    borderTopColor: COLORS.line,
    backgroundColor: '#06090A',
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: 24,
  } satisfies ViewStyle,
  footerLinks: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 18,
  } satisfies ViewStyle,
  footerLink: {
    color: COLORS.ink2,
    fontSize: 14,
    fontWeight: '600',
  } satisfies TextStyle,
};
